import {
  FormCheckBoxField,
  FormDateField,
  FormFieldContainer,
  FormNumberField,
  FormNumberInputAdornmentField,
  FormTriStateSwitchField,
  NewFormTextField,
  OptionsDataType,
  StaticFormSearchableSelectField,
  dateFormErrorName,
  useSetDirtyValue,
} from '../../../presentation';
import {
  SubmissionApplicationQuestionDetailsExt,
  SubmissionQuestionDetails,
} from './application-question';

import { Typography } from '@mui/material';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useRaterActionDispatchContext } from '../../underwriting/rater/data';
import { useTranslation } from 'react-i18next';

interface IApplicationQuestionComponentProps {
  question: SubmissionQuestionDetails | SubmissionApplicationQuestionDetailsExt;
  isDisabled: boolean;
  onBlur?: () => void;
  onChange?: (key: string, value: unknown) => void;
  good?: boolean;
  startNumber?: number;
  isPrimaryEndorsement?: boolean;
  setRaterError?: boolean;
}

export const ApplicationQuestionLabel = ({
  question,
  isDisabled,
  onBlur,
  isPrimaryEndorsement,
}: IApplicationQuestionComponentProps) => {
  const { t } = useTranslation();
  return (
    <FormFieldContainer md={12} data-question-uuid={question.uuid}>
      <Typography color="black">
        {t(
          getTranslationIdForQuestionLabel(question.uuid),
          question.question || question.uuid
        )}
      </Typography>
    </FormFieldContainer>
  );
};

export const ApplicationQuestionFormTextField = ({
  question,
  isDisabled,
  onBlur,
  onChange,
  isPrimaryEndorsement,
}: IApplicationQuestionComponentProps) => {
  const { t } = useTranslation();

  return (
    <NewFormTextField
      containerWidth={isPrimaryEndorsement ? 4.4 : 12}
      sideBySide
      label={t(
        getTranslationIdForQuestionLabel(question.uuid),
        question.question || question.uuid
      )}
      type="text"
      name={question.uuid}
      disabled={isDisabled}
      data-question-uuid={question.uuid}
      onBlur={() => onBlur && onBlur()}
      showToolbarEvenIfNotEditable
      isPrimaryEndorsement={isPrimaryEndorsement}
      className="application-question-input"
    />
  );
};

export const ApplicationQuestionFormNumberField = ({
  question,
  isDisabled,
  onBlur,
  onChange,
  startNumber,
  isPrimaryEndorsement,
}: IApplicationQuestionComponentProps) => {
  const { t } = useTranslation();
  const formMethods = useFormContext();
  useEffect(() => {
    if (question.uuid === '25767333-976c-4c05-913f-da45576dfbf8') {
      formMethods.setValue(
        question.uuid,
        question?.answer &&
          question?.answer?.answer &&
          question?.answer?.answer[0]?.length > 0
          ? question?.answer?.answer
          : 90
      );
    } else if (question.uuid === '122cf3d0-6cef-471c-bc37-93e2ddba4fc1') {
      formMethods.setValue(
        question.uuid,
        question?.answer &&
          question?.answer?.answer &&
          question?.answer?.answer[0]?.length > 0
          ? question?.answer?.answer
          : 80
      );
    } else if (question.uuid === 'a76fecd3-fc0b-4da4-8184-023b673d6a23') {
      formMethods.setValue(
        question.uuid,
        question?.answer &&
          question?.answer?.answer &&
          question?.answer?.answer[0]?.length > 0
          ? question?.answer?.answer
          : 20
      );
    } else if (question.uuid === '32e6975a-616c-42ac-8906-1a5e72e9ec06') {
      formMethods.setValue(
        question.uuid,
        question?.answer &&
          question?.answer?.answer &&
          question?.answer?.answer[0]?.length > 0
          ? question?.answer?.answer
          : 30
      );
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [question.uuid]);
  return (
    <FormNumberField
      isPrimaryEndorsement={isPrimaryEndorsement}
      isSameRow={true}
      containerWidth={12}
      label={t(
        getTranslationIdForQuestionLabel(question.uuid),
        question.question || question.uuid
      )}
      name={question.uuid}
      disabled={isDisabled}
      thousandSeparator=","
      data-question-uuid={question.uuid}
      onBlur={() => onBlur && onBlur()}
      startNumber={1}
      isAutomaticEndorsement={
        question.uuid === '25767333-976c-4c05-913f-da45576dfbf8'
      }
      isConsentToSettleEndorsementNewAcquiredEntity={
        question.uuid === '122cf3d0-6cef-471c-bc37-93e2ddba4fc1' ||
        question.uuid === 'a76fecd3-fc0b-4da4-8184-023b673d6a23' ||
        question.uuid === '32e6975a-616c-42ac-8906-1a5e72e9ec06'
      }
      // onChange={
      //   onChange
      //     ? (newValue) => {
      //         onChange(question.uuid, newValue);
      //       }
      //     : undefined
      // }
    />
  );
};

export const ApplicationQuestionFormCurrencyField = ({
  question,
  isDisabled,
  onBlur,
  onChange,
}: IApplicationQuestionComponentProps) => {
  const { t } = useTranslation();

  return (
    <FormNumberInputAdornmentField
      label={t(
        getTranslationIdForQuestionLabel(question.uuid),
        question.question || question.uuid
      )}
      name={question.uuid}
      disabled={isDisabled}
      adornmentText="$"
      thousandSeparator=","
      data-question-uuid={question.uuid}
      onBlur={() => onBlur && onBlur()}
      // onChange={
      //   onChange
      //     ? (newValue) => {
      //         onChange(question.uuid, newValue);
      //       }
      //     : undefined
      // }
    />
  );
};

export const ApplicationQuestionMultilineFormTextField = ({
  question,
  isDisabled,
  onBlur,
  onChange,
}: IApplicationQuestionComponentProps) => {
  const { t } = useTranslation();

  return (
    <NewFormTextField
      label={t(
        getTranslationIdForQuestionLabel(question.uuid),
        question.question || question.uuid
      )}
      containerWidth={12}
      name={question.uuid}
      disabled={isDisabled}
      isRichText
      data-question-uuid={question.uuid}
      onBlur={() => onBlur && onBlur()}
      showToolbarEvenIfNotEditable
      onChange={
        onChange
          ? (newValue) => {
              onChange(question.uuid, newValue);
            }
          : undefined
      }
    />
  );
};

export const ApplicationQuestionFormToggleField = ({
  question,
  isDisabled,
  onBlur,
  onChange,
  good,
}: IApplicationQuestionComponentProps) => {
  const { t } = useTranslation();

  const defaultAnswer = (): boolean | null | undefined => {
    let result;
    if (question?.answer?.answer === 'null') {
      result = null;
    } else if (question?.answer?.answer === 'true') {
      result = true;
    } else if (question?.answer?.answer === 'false') {
      result = false;
    }
    return result;
  };
  return (
    <FormTriStateSwitchField
      label={t(
        getTranslationIdForQuestionLabel(question.uuid),
        question.question || question.uuid
      )}
      defaultValue={defaultAnswer()}
      name={question.uuid}
      disabled={isDisabled}
      data-question-uuid={question.uuid}
      onBlur={() => onBlur && onBlur()}
      // if question does not exist it use default true
      good={question.good ?? true}
      onChangeValue={
        onChange
          ? (newValue) => {
              onChange(question.uuid, newValue);
            }
          : undefined
      }
    />
  );
};

export const ApplicationQuestionFormCheckboxField = ({
  question,
  isDisabled,
  onBlur,
  onChange,
}: IApplicationQuestionComponentProps) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const { setDirtyValue } = useSetDirtyValue();
  const checked = watch(question.uuid);

  return (
    <FormCheckBoxField
      label={t(
        getTranslationIdForQuestionLabel(question.uuid),
        question.question || question.uuid
      )}
      name={question.uuid}
      disabled={isDisabled}
      data-question-uuid={question.uuid}
      handleChange={(event) => {
        setDirtyValue(question.uuid, event.target.checked);
      }}
      customWeight="400"
      checked={checked}
      labelPlacement="start"
      size="medium"
      fullWidth
      onBlur={() => onBlur && onBlur()}
      onChange={(event) => {
        setDirtyValue(question.uuid, event.target.checked);
      }}
    />
  );
};

export const ApplicationQuestionFormSelectField = ({
  question,
  isDisabled,
  onBlur,
  onChange,
}: IApplicationQuestionComponentProps) => {
  const { t } = useTranslation();
  return (
    <StaticFormSearchableSelectField
      label={t(
        getTranslationIdForQuestionLabel(question.uuid),
        question.question || question.uuid
      )}
      name={question.uuid}
      optionsType={question.uuid as OptionsDataType}
      disabled={isDisabled}
      data-question-uuid={question.uuid}
      onBlur={() => onBlur && onBlur()}
      onChangeOption={(option) => {
        if (onChange) {
          onChange(question.uuid, option);
        }
      }}
    />
  );
};

export const ApplicationQuestionFormDateField = ({
  question,
  isDisabled,
  onBlur,
  onChange,
  isPrimaryEndorsement,
  setRaterError,
}: IApplicationQuestionComponentProps) => {
  const { t } = useTranslation();
  const { dispatch, actions } = useRaterActionDispatchContext();

  const {
    formState: { errors },
  } = useFormContext();
  const hasError = errors[dateFormErrorName] ? true : false;

  useEffect(() => {
    if (setRaterError) dispatch(actions.setIsInvalidRaterData(hasError));
  }, [actions, dispatch, setRaterError, hasError]);

  return (
    <FormDateField
      label={t(
        getTranslationIdForQuestionLabel(question.uuid),
        question.question || question.uuid
      )}
      name={question.uuid}
      disabled={isDisabled}
      isPrimaryEndorsement={isPrimaryEndorsement}
      data-question-uuid={question.uuid}
      onDateChange={(newValue) => {
        onBlur && onBlur();
        onChange && onChange(question.uuid, newValue);
      }}
    />
  );
};

export const ApplicationQuestionFormMonthField = ({
  question,
  isDisabled,
  onBlur,
  onChange,
}: IApplicationQuestionComponentProps) => {
  const { t } = useTranslation();

  return (
    <FormDateField
      label={t(
        getTranslationIdForQuestionLabel(question.uuid),
        question.question || question.uuid
      )}
      name={question.uuid}
      views={['year', 'month']}
      disabled={isDisabled}
      data-question-uuid={question.uuid}
      onBlur={() => onBlur && onBlur()}
      onDateChange={(newValue) => {
        onChange && onChange(question.uuid, newValue);
      }}
    />
  );
};

const getTranslationIdForQuestionLabel = (questionId: string) => {
  return `application.questions.${questionId}` as const;
};
